import Modal from "@/common/components/Modal"
import { zodResolver } from "@hookform/resolvers/zod";
import { BadgePlus } from "lucide-react"
import { PrivateClientsFunnelValidationSchema, createSchema } from "../schemas/create.schema";
import TextField from "@/common/components/TextField";
import TextArea from "@/common/components/TextArea";
import { useQueryClient } from "@tanstack/react-query";
import MaskedTextField from "@/common/components/MaskedTextField";
import { cnpjCpfMask, phoneMask } from "@/common/constants/masks.constant";
import { useState } from "react";
import useCreatePrivateCompanyLead from "../hooks/useCreatePrivateCompanyLead";
import { FormTransformers } from "@/common/utils/formTransformers";
import Button from "../../../common/components/Button";
import { DirtySubmitHandler, useCustomForm } from "@/common/hooks/useCustomForm";

const CreateModal = () => {
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false);
  const [serverError, setServerError] = useState(false);

  const {
    register,
    handleDirtySubmit,
    reset: resetForm,
    formState: { errors },
    setError
  } = useCustomForm<PrivateClientsFunnelValidationSchema>({
    resolver: zodResolver(createSchema)
  })

  const onSubmit: DirtySubmitHandler<PrivateClientsFunnelValidationSchema> = (data) => createPrivateCompanyLead(data);

  const onSuccess = () => {
    resetForm()
    setServerError(false)
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLeads'] })
    setIsOpen(false)
  }

  const {
    mutate: createPrivateCompanyLead,
    isPending: isCreatePrivateCompanyLeadPending,
  } = useCreatePrivateCompanyLead(onSuccess,
    FormTransformers.errorsTransformer<PrivateClientsFunnelValidationSchema>({ setError }));

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleDirtySubmit(onSubmit)}
      Trigger={<Button name="open_create_lead_modal" className="bg-transparent hover:bg-transparent p-0"
        operationKlassToCheckPermission={"Admin::Funnel::PrivateCompanyLeads::Create"}><BadgePlus color="#7CC4F8" strokeWidth={1.5} /></Button>}
      title='Cadastrando oportunidade'
      dataTestid="create_private_company_funnel"
      footer="Cadastrar"
      disabled={isCreatePrivateCompanyLeadPending}
      confirmButtonIsLoading={isCreatePrivateCompanyLeadPending}
      body={(
        <form className="flex flex-col gap-4">
          {serverError && <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>}
          <div className="flex gap-4">
            <TextField
              className="w-full"
              label="Nome da empresa"
              markAsRequired
              errorMessage={errors?.display_name?.message || undefined}
              data-testid="display_name_input"
              {...register('display_name')}
            />
            <MaskedTextField
              className="w-full"
              label="CNPJ/CPF"
              markAsRequired
              mask={cnpjCpfMask}
              errorMessage={errors?.general_information?.cnpj?.message || undefined}
              {...register('general_information.cnpj')}
            />
          </div>
          <div className="flex gap-4">
            <TextField
              className="w-full"
              label="Nome do Contato"
              markAsRequired
              errorMessage={errors?.contacts?.full_name?.message}
              data-testid="full_name_input"
              {...register('contacts.full_name')}
            />
            <TextField
              className="w-full"
              label="Cargo"
              maxLength={32}
              errorMessage={errors?.contacts?.role_in_company?.message}
              data-testid="role_in_company_input"
              {...register('contacts.role_in_company')}
            />
          </div>
          <div className="flex gap-4">
            <MaskedTextField
              className="w-full"
              label="Telefone do Contato"
              markAsRequired
              mask={phoneMask}
              errorMessage={errors?.contacts?.phone?.number?.message}
              {...register('contacts.phone.number')}
            />
            <TextField
              className="w-full"
              label="Email de Contato"
              markAsRequired
              errorMessage={errors?.contacts?.email?.message}
              data-testid="email_input"
              {...register('contacts.email')}
            />
          </div>
          <TextArea
            label="Suas observações"
            markAsRequired
            maxLength={1500}
            errorMessage={errors?.observations?.message}
            data-testid="observations_input"
            {...register('observations')}
          />
        </form>
      )}
    />
  )
}

export default CreateModal
